import { request } from '@umijs/max';
import type { ApiPageResult } from '@/services/apiResponse';
import { getNebulithUserApiUrl } from '@/services/util';

export interface UserRoleListItem {
  roleId: string;
  name: string;
  description?: string;
  status?: string;
  createAt?: string;
  createBy?: string;
  updateAt?: string;
  updateBy?: string;
  createUser?: UserOperatorRef;
  updateUser?: UserOperatorRef;
}

export interface UserTenantRef {
  tenantId: string;
  displayName: string;
  description?: string;
  status?: string;
  userCount?: number;
  createAt?: string;
  createBy?: string;
  updateAt?: string;
  updateBy?: string;
  createUser?: UserOperatorRef;
  updateUser?: UserOperatorRef;
}

export interface UserOperatorRef {
  userId?: string;
  email?: string;
  username?: string;
  displayName?: string;
  status?: string;
}

export interface UserListItem {
  userId: string;
  username: string;
  displayName: string;
  email: string;
  status: string;
  createStatus?: string;
  tenantId?: string;
  tenant?: UserTenantRef;
  roleIds?: string[];
  roleList?: UserRoleListItem[];
  createBy?: string;
  updateBy?: string;
  createAt?: string;
  updateAt?: string;
  createUser?: UserOperatorRef;
  updateUser?: UserOperatorRef;
}

export interface UserListParams {
  username?: string;
  email?: string;
  status?: string;
  tenantId?: string;
}

export interface UserListPageParams {
  username?: string;
  email?: string;
  status?: string;
  tenantId?: string;
  page: {
    pageNo: number;
    pageSize: number;
  };
}

export type UserListPageResult = ApiPageResult<UserListItem>;

export interface UserListResult {
  list: UserListItem[];
}

export interface CreateUserParams {
  username: string;
  displayName: string;
  email: string;
  password: string;
  status: string;
  tenantId: string;
}

export interface UpdateUserParams {
  userId: string;
  username: string;
  displayName: string;
  email: string;
  status: string;
  tenantId: string;
}

export interface DeleteUserParams {
  userId: string;
}

export interface UpdateUserStatusParams {
  userId: string;
  status: string;
}

export interface ChangePasswordParams {
  oldPassword: string;
  newPassword: string;
}

export interface UserSelectOption {
  label: string;
  value: string;
}

export type UserDisplayRef = UserOperatorRef;

export function formatUserListRoleText(record: UserListItem, fallback = '--') {
  const roleNames = record.roleList?.map((item) => item.name).filter(Boolean);
  return roleNames?.length ? roleNames.join('、') : fallback;
}

export function formatUserDisplayName(
  user?: UserDisplayRef | string | null,
  fallback = '-',
): string {
  if (!user) {
    return fallback;
  }
  if (typeof user === 'string') {
    return user.trim() || fallback;
  }
  return user.displayName || user.username || user.userId || fallback;
}

export function mapUserListToOptions(
  users: UserDisplayRef[] = [],
): UserSelectOption[] {
  return users
    .filter((user) => Boolean(user.userId))
    .map((user) => ({
      label: user.displayName || user.username || user.userId || '',
      value: user.userId as string,
    }));
}

export function getRecordUserField(
  record: Record<string, unknown>,
  userField: string | string[],
): UserDisplayRef | undefined {
  let value: unknown = record;
  const path = Array.isArray(userField) ? userField : [userField];
  for (const key of path) {
    value = (value as Record<string, unknown> | undefined)?.[key];
  }
  return value as UserDisplayRef | undefined;
}

/** 用户列表 */
export async function getUserList(
  params?: UserListParams,
  options?: { [key: string]: unknown },
) {
  return request<UserListResult>(getNebulithUserApiUrl('user/list'), {
    method: 'POST',
    data: params || {},
    ...(options || {}),
  });
}

/** 用户分页列表 */
export async function getUserListPage(
  params: UserListPageParams,
  options?: { [key: string]: any },
) {
  return request<UserListPageResult>(getNebulithUserApiUrl('user/list-page'), {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 新增用户 */
export async function createUser(
  params: CreateUserParams,
  options?: { [key: string]: any },
) {
  return request<Record<string, any>>(getNebulithUserApiUrl('user/create'), {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 编辑用户 */
export async function updateUser(
  params: UpdateUserParams,
  options?: { [key: string]: any },
) {
  return request<Record<string, any>>(getNebulithUserApiUrl('user/update'), {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 删除用户 */
export async function deleteUser(
  params: DeleteUserParams,
  options?: { [key: string]: any },
) {
  return request<Record<string, any>>(getNebulithUserApiUrl('user/delete'), {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 修改当前用户密码 */
export async function changePassword(
  params: ChangePasswordParams,
  options?: { [key: string]: any },
) {
  return request<Record<string, any>>(
    getNebulithUserApiUrl('user/change-password'),
    {
      method: 'POST',
      data: params,
      ...(options || {}),
    },
  );
}

/** 启用/停用用户 */
export async function updateUserStatus(
  params: UpdateUserStatusParams,
  options?: { [key: string]: any },
) {
  return request<Record<string, any>>(
    getNebulithUserApiUrl('user/update-status'),
    {
      method: 'POST',
      data: params,
      ...(options || {}),
    },
  );
}

export interface ResetUserPasswordParams {
  userId: string;
  newPassword: string;
}

/** 重置用户密码 */
export async function resetUserPassword(
  params: ResetUserPasswordParams,
  options?: { [key: string]: any },
) {
  return request<Record<string, any>>(
    getNebulithUserApiUrl('user/reset-password'),
    {
      method: 'POST',
      data: params,
      ...(options || {}),
    },
  );
}
